import styles from './activity.module.css';
import { ActivityPage, ActivityRewardCard } from './activity-cards';
import { ProgressBar } from '../../../components/progress-bar';
import { Pill, type PillTone } from '../../../components/pill';
import { Card } from '../../../components/card';
import { PageHead } from '../../../components/page-head';

import { formatDate, formatDateTime } from '../../../utils/format-date';
import { Modal } from '../../../components/modal';
import { Button } from '../../../components/button';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import defaultAvatar from '../../../assets/icons/avatars/default-avatar.svg';
import { useTranslation } from 'react-i18next';

import {

  useGameplayContext,

  useSubmitGameplayReflection,
} from '../../../api/student/campaigns/hooks';
import type {
  StudentActivity,
  GameplayPostSessionContext,
  GameplayTeammate,
} from '../../../api/student/campaigns/types';
import { isActivityFullyDone, isMissedSession } from '../../../api/student/campaigns/gating';

import { useSessionLaunch } from '../use-session-launch';
import { endorsementTag } from '../endorsement-tag';
import { SESSION_LOOK } from '../session-look';
import { sessionShown } from '../../../api/student/session-attendance';

/** Answer-strength level → its words, a key written whole, and its accent class. */
const QUALITY: Record<'tooShort' | 'weak' | 'medium' | 'good', { key: string; accent: string }> = {
  tooShort: { key: 'student.campaign.gameplay.answerQuality.tooShort', accent: styles.studentGameplay__answerQualityTooShort },
  weak: { key: 'student.campaign.gameplay.answerQuality.weak', accent: styles.studentGameplay__answerQualityWeak },
  medium: { key: 'student.campaign.gameplay.answerQuality.medium', accent: styles.studentGameplay__answerQualityMedium },
  good: { key: 'student.campaign.gameplay.answerQuality.good', accent: styles.studentGameplay__answerQualityGood },
};

/**
 * Gameplay — launching a session, and everything that happens after one: the
 * post-session reflection and teammate endorsements.
 *
 * The largest of the campaign views by some way, which is the clearest argument
 * for it having its own file. Where the session stands picks the body and the
 * reward card beside it; the page around them (ActivityPage) is drawn once.
 */
export function GameplayView({
  campaignId,
  activity,
  noMoreSessions,
  kpBalance,
  shutNote,
  onCompleted,
}: {
  campaignId: string;
  activity: StudentActivity;
  /** Its campaign's live sessions are over and none came for it (noMoreSessions, gating.ts): nothing can complete it now. */
  noMoreSessions: boolean;
  kpBalance: number;
  /** Why it cannot be completed yet, while its chapter is shut: said in its reward card. */
  shutNote: string | null;
  onCompleted: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const { enter, launchingId, error: launchError } = useSessionLaunch();
  const session = activity.worldSession;

  const resultJson = activity.completion?.resultJson as Record<string, unknown> | null | undefined;
  const sessionFinished = session?.status === 'FINISHED';
  const isMissed = sessionFinished && isMissedSession(activity);
  const reflectionDone = sessionFinished && !isMissed && !!(resultJson?.reflection);

  // Post-session context (loaded only when the session is FINISHED + participated).
  const contextQuery = useGameplayContext(
    campaignId,
    activity.id,
    Boolean(sessionFinished && !isMissed && activity.completion),
  );
  const context = contextQuery.data ?? null;
  const contextLoading = contextQuery.isLoading;
  const contextError = contextQuery.error instanceof Error ? contextQuery.error.message : null;


  // Where the session stands: the body it gives the page, and the reward card beside it.
  let body: ReactNode;
  let reward: ReactNode = <ActivityRewardCard activity={activity} shutNote={shutNote} />;

  if (noMoreSessions) {
    // Past its campaign's end, with no session played: it says there are no
    // more, where it would have said locked, and points to what can still be done.
    body = (
      <SessionNotice
        tone="info"
        label={t('student.campaign.gameplay.sessionsOver')}
        title={t('student.campaign.gameplay.noMoreSessions')}
        detail={t('student.campaign.gameplay.noMoreSessionsDetail')}
      />
    );
    reward = null;
  } else if (!session) {
    body = (
      <Card framed className={`${styles.studentArticle__body}`}>
        <p>{t('student.campaigns.gameplayMissing')}</p>
      </Card>
    );
  } else if (session.status === 'DRAFT' || session.startTimeUtc === null) {
    body = (
      <Card framed className={`${styles.studentArticle__body}`}>
        <p>{t('student.campaigns.gameplayNotScheduled')}</p>
      </Card>
    );
  } else if (isMissed) {
    // ── Missed: the session ended without the student, and nothing can
    // complete the activity now, so no reward card offers to ──
    body = (
      <SessionNotice
        tone="warning"
        label={t('student.campaign.gameplay.sessionClosed')}
        title={t('student.campaign.gameplay.missedSession')}
        detail={t('student.campaign.gameplay.missedSessionDetail')}
      />
    );
    reward = null;
  } else if (reflectionDone) {
    // ── Fully done (reflection submitted) ──
    const reflection = resultJson?.reflection as { answers?: Record<string, string>; submittedAt?: string } | undefined;
    const answers = reflection?.answers ?? {};
    body = (
      <div>
        <Card framed className={styles.studentGameplay__done}>
          <p className={styles.studentGameplay__doneCheck}>✓ {t('student.campaign.gameplay.reflectionDone')}</p>
          {reflection?.submittedAt && (
            <p className={styles.studentGameplay__doneDate}>
              {formatDate(reflection.submittedAt)}
            </p>
          )}
        </Card>

        {/* Read-only reflection answers */}
        {context && context.reflectionQuestions.length > 0 && (
          <Card framed className={`${styles.studentGameplay__reflection}`}>
            <PageHead level="section" tight>{t('student.campaign.gameplay.reflectionTitle')}</PageHead>
            {context.reflectionQuestions.map((q) => (
              <div key={q.id} className={`${styles.studentGameplay__question}`}>
                <p className={`${styles.studentGameplay__questionLabel}`}>{q.prompt}</p>
                <p className={styles.studentGameplay__answerReadonly}>{answers[q.id] ?? '—'}</p>
              </div>
            ))}
          </Card>
        )}

        {/* Read-only endorsements: the record of the one given, or of none */}
        {context && context.teammates.length > 0 && (
          <Card framed className={`${styles.studentGameplay__endorsements}`}>
            <PageHead level="section" tight>{t('student.campaign.gameplay.endorsementRecordTitle')}</PageHead>
            {context.alreadyEndorsed.length === 0 && (
              <p className={`${styles.studentGameplay__endorsementsDesc}`}>{t('student.campaign.gameplay.noEndorsementGiven')}</p>
            )}
            <div className={`${styles.studentGameplay__teammatesGrid}`}>
              {context.teammates.map((tm) => (
                <TeammateEndorsementRecord
                  key={tm.nakamaId}
                  teammate={tm}
                  tagId={context.alreadyEndorsed.find((e) => e.nakamaId === tm.nakamaId)?.tagId ?? null}
                />
              ))}
            </div>
          </Card>
        )}
      </div>
    );
    reward = <ActivityRewardCard activity={activity} done />;
  } else if (!sessionFinished) {
    // ── Session not finished yet — show session info + join button ──
    // Where it stands, as the sessions page's cards say it: their pill and words, not the server's word.
    const look = SESSION_LOOK[sessionShown(session.status)];
    body = (
      <Card framed className={`${styles.studentArticle__body}`}>
        <p><strong>{t('student.campaigns.scheduledFor')}:</strong> {formatDateTime(session.startTimeUtc)}</p>
        <p>
          <strong>{t('student.campaigns.gameplayStatus')}:</strong> <Pill tone={look.tone}>{t(look.key)}</Pill>
        </p>
        <p className={`${styles.studentGameplay__kpReminder}`}>
          {t('student.progress.kpBalance', { balance: kpBalance })}
        </p>
        {launchError && <p className="tone-danger">{launchError}</p>}
      </Card>
    );
    // While it runs, the way in is the card's action. One already done says so
    // there by what it paid (isActivityFullyDone, as the chapter's trail counts
    // it), not by a completion row alone: a session's row waits on the reflection.
    reward = (
      <ActivityRewardCard
        activity={activity}
        done={isActivityFullyDone(activity)}
        shutNote={shutNote}
        action={session.status === 'RUNNING' ? { words: t('student.campaigns.enterMatch'), onClick: () => void enter(session.id), busy: launchingId !== null } : null}
      />
    );
  } else {
    // ── Session finished, participated — show post-session reflection phase ──
    body = (
      <div>
        {contextLoading && <p className="tone-soft">{t('common.loading')}</p>}
        {contextError && <p className="tone-danger">{contextError}</p>}
        {context && (
          <PostSessionReflectionView
            campaignId={campaignId}
            activityId={activity.id}
            context={context}
            onSubmitted={onCompleted}
          />
        )}
      </div>
    );
  }

  return (
    <ActivityPage activity={activity} reward={reward}>
      {body}
    </ActivityPage>
  );
}

/** Why the session can no longer be played — its campaign's are over, or it ended without the student: a pill, what happened, and what it means. */
function SessionNotice({ tone, label, title, detail }: { tone: PillTone; label: string; title: string; detail: string }) {
  return (
    <Card framed className={`${styles.studentGameplay__notice}`}>
      <Pill tone={tone}>{label}</Pill>
      <p className={`${styles.studentGameplay__noticeTitle}`}>{title}</p>
      <p className={`${styles.studentGameplay__noticeDetail}`}>{detail}</p>
    </Card>
  );
}

// ── Post-session reflection view ──────────────────────────────────────────

function PostSessionReflectionView({
  campaignId,
  activityId,
  context,
  onSubmitted,
}: {
  campaignId: string;
  activityId: string;
  context: GameplayPostSessionContext;
  onSubmitted: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [endorsements, setEndorsements] = useState<{ nakamaId: string; tagId: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const reflection = useSubmitGameplayReflection();
  const submitting = reflection.isPending;

  const endorsementsUsed = endorsements.length;
  const endorsementsMax = 1;
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const ANSWER_TOO_SHORT_WORDS = 5;   // below this: tooShort (submit blocked)
  const ANSWER_WEAK_WORDS      = 10;  // below this: weak
  const ANSWER_MEDIUM_WORDS    = 15;  // below this: medium; at/above: good (bar full green)
  const ANSWER_FLOOR_WORDS     = ANSWER_TOO_SHORT_WORDS;
  const ANSWER_GOAL_WORDS      = ANSWER_MEDIUM_WORDS;

  function countWords(text: string): number {
    return text.trim().split(/\s+/).filter(Boolean).length;
  }

  function handleAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  function handleEndorse(nakamaId: string, tagId: string) {
    setEndorsements((prev) => {
      const existing = prev.findIndex((e) => e.nakamaId === nakamaId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { nakamaId, tagId };
        return updated;
      }
      if (prev.length >= endorsementsMax) return prev;
      return [...prev, { nakamaId, tagId }];
    });
  }

  function handleRemoveEndorsement(nakamaId: string) {
    setEndorsements((prev) => prev.filter((e) => e.nakamaId !== nakamaId));
  }

  function handleSubmitClick(e: React.FormEvent) {
    e.preventDefault();
    for (const q of context.reflectionQuestions) {
      if (countWords(answers[q.id] ?? '') < ANSWER_FLOOR_WORDS) {
        setError(t('student.campaign.gameplay.answerTooShort'));
        return;
      }
    }
    setError(null);
    setConfirmOpen(true);
  }

  function handleConfirmSubmit() {
    setConfirmOpen(false);
    setError(null);
    reflection.mutate(
      { campaignId, activityId, answers, endorsements },
      {
        onSuccess: () => { void onSubmitted(); },
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  }

  return (
    <>
      {confirmOpen && (
        <Modal
          isOpen
          onClose={() => setConfirmOpen(false)}
          size="sm"
          layer="confirm"
          title={t('student.campaign.gameplay.confirmTitle')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => void handleConfirmSubmit()}>
                {t('student.campaign.gameplay.confirmSubmit')}
              </Button>
            </>
          }
        >
          <p>{t('student.campaign.gameplay.confirmBody')}</p>
        </Modal>
      )}

    <form onSubmit={handleSubmitClick} className={`${styles.studentGameplay__postSession}`}>
      <Card framed className={`${styles.studentGameplay__phaseBanner}`}>
        <p>{t('student.campaign.gameplay.awaitingReflection')}</p>
      </Card>

      {/* Reflection questions */}
      {context.reflectionQuestions.length > 0 && (
        <Card framed className={`${styles.studentGameplay__reflection}`}>
          <PageHead level="section" tight>{t('student.campaign.gameplay.reflectionTitle')}</PageHead>
          {context.reflectionQuestions.map((q) => {
            const val = answers[q.id] ?? '';
            const wordCount = val.trim() ? countWords(val) : 0;
            const strength = Math.min(wordCount / ANSWER_GOAL_WORDS, 1);
            const qualityLevel =
              wordCount === 0                      ? null
              : wordCount < ANSWER_TOO_SHORT_WORDS ? 'tooShort'
              : wordCount < ANSWER_WEAK_WORDS      ? 'weak'
              : wordCount < ANSWER_MEDIUM_WORDS    ? 'medium'
              : 'good';
            return (
              <div key={q.id} className={`${styles.studentGameplay__question}`}>
                <label htmlFor={`q-${q.id}`} className={`${styles.studentGameplay__questionLabel}`}>{q.prompt}</label>
                <textarea
                  id={`q-${q.id}`}
                  className={`${styles.studentGameplay__textarea}`}
                  rows={3}
                  value={val}
                  onChange={(e) => handleAnswer(q.id, e.target.value)}
                  disabled={submitting}
                />
                <ProgressBar value={strength * 100} grow minVisible={4} />
                {qualityLevel && (
                  <span className={`${styles.studentGameplay__answerQuality} ${QUALITY[qualityLevel].accent}`}>
                    {t(QUALITY[qualityLevel].key)}
                  </span>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {/* Peer endorsements (optional) */}
      {context.teammates.length > 0 && (
        <Card framed className={`${styles.studentGameplay__endorsements}`}>
          <div className={`${styles.studentGameplay__endorsementsHeader}`}>
            <PageHead level="section" tight>{t('student.campaign.gameplay.endorsementsTitle')}</PageHead>
            <Pill tone="locked">{t('student.campaign.gameplay.optional')}</Pill>
          </div>
          <p className={`${styles.studentGameplay__endorsementsDesc}`}>
            {t('student.campaign.gameplay.endorsementsDesc')}
          </p>
          <p className={`${styles.studentGameplay__endorsementsCounter}`}>
            {t('student.campaign.gameplay.endorsementsCounter', { used: endorsementsUsed, total: endorsementsMax })}
          </p>
          <div className={`${styles.studentGameplay__teammatesGrid}`}>
            {context.teammates.map((tm) => (
              <TeammateEndorsementCard
                key={tm.nakamaId}
                teammate={tm}
                availableTags={context.availableTags}
                currentEndorsement={endorsements.find((e) => e.nakamaId === tm.nakamaId) ?? null}
                canEndorse={endorsementsUsed < endorsementsMax || !!endorsements.find((e) => e.nakamaId === tm.nakamaId)}
                alreadyEndorsed={context.alreadyEndorsedNakamaIds.includes(tm.nakamaId)}
                isOpen={openCardId === tm.nakamaId}
                onOpenToggle={(id) => setOpenCardId((prev) => (prev === id ? null : id))}
                onEndorse={(nakamaId, tagId) => { handleEndorse(nakamaId, tagId); setOpenCardId(null); }}
                onRemove={handleRemoveEndorsement}
              />
            ))}
          </div>
        </Card>
      )}

      {error && <p className="tone-danger">{error}</p>}
      <Button className={`${styles.studentGameplay__submit}`} type="submit" disabled={submitting}>
        {submitting ? t('common.loading') : t('student.campaign.gameplay.reflectionSubmit')}
      </Button>
    </form>
    </>
  );
}

/**
 * A teammate card's look, one at a time: endorsed now; endorsed in an earlier
 * session (locked: settled, nothing to click); still to choose from (idle:
 * muted until the pointer or the focus reaches it), or at full while its tags
 * are open; and out of endorsements (disabled). Derived once, so no two can
 * stack, as idle and disabled once did.
 */
type TeammateLook = 'endorsed' | 'locked' | 'open' | 'idle' | 'disabled';

const TEAMMATE_LOOK: Record<TeammateLook, string> = {
  endorsed: styles.studentGameplay__teammateCardEndorsed,
  locked: styles.studentGameplay__teammateCardLocked,
  open: '',
  idle: styles.studentGameplay__teammateCardIdle,
  disabled: styles.studentGameplay__teammateCardDisabled,
};

function TeammateEndorsementCard({
  teammate,
  availableTags,
  currentEndorsement,
  canEndorse,
  alreadyEndorsed,
  isOpen,
  onOpenToggle,
  onEndorse,
  onRemove,
}: {
  teammate: GameplayTeammate;
  availableTags: { id: string }[];
  currentEndorsement: { nakamaId: string; tagId: string } | null;
  canEndorse: boolean;
  alreadyEndorsed: boolean;
  isOpen: boolean;
  onOpenToggle: (nakamaId: string) => void;
  onEndorse: (nakamaId: string, tagId: string) => void;
  onRemove: (nakamaId: string) => void;
}) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);

  const look: TeammateLook = alreadyEndorsed
    ? 'locked'
    : currentEndorsement
      ? 'endorsed'
      : !canEndorse
        ? 'disabled'
        : isOpen
          ? 'open'
          : 'idle';

  // Close popover when clicking outside this card.
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onOpenToggle(teammate.nakamaId);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenToggle, teammate.nakamaId]);

  function handleAvatarClick() {
    if (look === 'endorsed') onRemove(teammate.nakamaId);
    else if (look === 'idle' || look === 'open') onOpenToggle(teammate.nakamaId);
  }

  const cardClass = [styles.studentGameplay__teammateCard, TEAMMATE_LOOK[look]].filter(Boolean).join(' ');

  return (
    <div className={cardClass} ref={cardRef}>
      <button
        type="button"
        className={`${styles.studentGameplay__teammateAvatarBtn}`}
        onClick={handleAvatarClick}
        aria-label={teammate.displayName}
        title={look === 'locked' ? t('student.campaign.gameplay.endorsedLabel') : look === 'endorsed' ? t('student.campaign.gameplay.removeEndorsement') : t('student.campaign.gameplay.endorseButton')}
      >
        <TeammateFace
          teammate={teammate}
          tagId={look === 'endorsed' ? (currentEndorsement?.tagId ?? null) : null}
          earlier={look === 'locked'}
        />
      </button>

      <span className={`${styles.studentGameplay__teammateName}`}>{teammate.displayName}</span>

      {look === 'open' && (
        <div className={`${styles.studentGameplay__tagChips}`}>
          {availableTags.map((tag) => {
            const { name, emoji } = endorsementTag(t, tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                className={`${styles.studentGameplay__tagChip}`}
                onClick={() => onEndorse(teammate.nakamaId, tag.id)}
                title={name}
              >
                <span className={styles.studentGameplay__tagChipEmoji}>{emoji}</span>
                <span className={styles.studentGameplay__tagChipLabel}>{name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A teammate's face: their avatar, and a badge for an endorsement — its tag,
 * named for a screen reader, or a tick for one given in an earlier session.
 */
function TeammateFace({ teammate, tagId, earlier = false }: { teammate: GameplayTeammate; tagId: string | null; earlier?: boolean }) {
  const { t } = useTranslation();
  const tag = tagId ? endorsementTag(t, tagId) : null;
  return (
    <>
      <img
        src={teammate.avatarPhotoUrl || defaultAvatar}
        alt={teammate.displayName}
        className={`${styles.studentGameplay__teammateAvatar}`}
      />
      {tag ? (
        <span className={`${styles.studentGameplay__teammateBadge}`} role="img" aria-label={tag.name}>
          {tag.emoji}
        </span>
      ) : earlier ? (
        <span className={`${styles.studentGameplay__teammateBadge} ${styles.studentGameplay__teammateBadgeLocked}`}>✓</span>
      ) : null}
    </>
  );
}

/**
 * A teammate as a submitted reflection records them: the one endorsed, with
 * its tag, and the rest muted. Drawn static — no button, no pointer, no title
 * offering an action — as nothing is left to choose.
 */
function TeammateEndorsementRecord({ teammate, tagId }: { teammate: GameplayTeammate; tagId: string | null }) {
  const look = tagId ? styles.studentGameplay__teammateCardEndorsed : styles.studentGameplay__teammateCardStatic;
  return (
    <div className={`${styles.studentGameplay__teammateCard} ${look}`}>
      <span className={`${styles.studentGameplay__teammateFace}`}>
        <TeammateFace teammate={teammate} tagId={tagId} />
      </span>
      <span className={`${styles.studentGameplay__teammateName}`}>{teammate.displayName}</span>
    </div>
  );
}
